import React from 'react';
import herobg from '../assets/herobg.jpg';

const Services = () => {
  const services = [
    {
      title: "Custom Software Development",
      description: "Tailored solutions built to meet your specific business requirements and scale with your growth.",
      features: ["Requirements Analysis", "Custom Architecture", "Scalable Solutions", "Ongoing Support"]
    },
    {
      title: "Mobile App Development", 
      description: "Native and cross-platform mobile applications that deliver exceptional user experiences.",
      features: ["iOS Development", "Android Development", "Cross-Platform", "App Store Optimization"]
    },
    {
      title: "Web Application Development",
      description: "Modern, responsive web applications built with cutting-edge technologies and best practices.",
      features: ["React/Vue/Angular", "Progressive Web Apps", "E-commerce Solutions", "API Development"]
    },
    {
      title: "Cloud Solutions",
      description: "Scalable cloud infrastructure and migration services to optimize your operations.",
      features: ["Cloud Migration", "AWS/Azure/GCP", "DevOps Implementation", "Cloud Security"]
    },
    {
      title: "Data Analytics & AI",
      description: "Transform your data into actionable insights with advanced analytics and AI solutions.",
      features: ["Machine Learning", "Data Visualization", "Predictive Analytics", "AI Integration"]
    },
    {
      title: "Cybersecurity",
      description: "Comprehensive security solutions to protect your digital assets and ensure compliance.",
      features: ["Security Audits", "Penetration Testing", "Compliance Management", "Security Training"]
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section 
        className="relative min-h-screen flex items-center justify-center overflow-hidden"
        style={{
          backgroundImage: `linear-gradient(rgba(218, 212, 212, 0.7), rgba(19, 1, 66, 0.8)), url(${herobg})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed'
        }}
      >
        <div className="relative z-10 text-center px-6 max-w-5xl mx-auto">
          <h1 className="text-5xl md:text-7xl font-bold mb-6 fade-in animate-fade-in-up">
            Our <span className="text-navy-blue">Services</span>
          </h1>
          
          <p className="text-xl md:text-2xl text-white mb-8 max-w-3xl mx-auto fade-in animate-fade-in-up" style={{ animationDelay: '0.3s' }}>
            Comprehensive technology solutions tailored to transform your business
          </p>
        </div>
      </section>

      {/* Services Details */}
      <section className="py-20 relative">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div
                key={index}
                className="bg-white border border-gray-200 rounded-xl p-8 shadow-lg card-hover fade-in"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <h3 className="text-2xl font-bold mb-4 text-dark-red">
                  {service.title}
                </h3>
                <p className="text-black mb-6 leading-relaxed">
                  {service.description}
                </p>
                <ul className="space-y-2">
                  {service.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center text-black text-sm">
                      <svg className="w-4 h-4 text-navy-blue mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Overview */}
      <section className="py-20 relative">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16 fade-in">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              Our <span className="text-navy-blue">Process</span>
            </h2>
            <p className="text-xl text-black max-w-3xl mx-auto">
              A structured approach to ensure successful project delivery
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              { step: "01", title: "Discovery", desc: "Understanding your needs and goals" },
              { step: "02", title: "Design", desc: "Creating user-centric solutions" },
              { step: "03", title: "Development", desc: "Building with best practices" },
              { step: "04", title: "Delivery", desc: "Launching and supporting your solution" }
            ].map((item, index) => (
              <div key={index} className="text-center fade-in" style={{ animationDelay: `${index * 0.1}s` }}>
                <div className="w-16 h-16 bg-gradient-to-br from-dark-red to-navy-blue rounded-full flex items-center justify-center text-white font-bold text-xl mx-auto mb-4">
                  {item.step}
                </div>
                <h3 className="text-xl font-bold text-dark-red mb-2">{item.title}</h3>
                <p className="text-black text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technology Stack */}
      <section className="py-20 relative">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16 fade-in">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              Technology <span className="text-navy-blue">Expertise</span>
            </h2>
            <p className="text-xl text-black max-w-3xl mx-auto">
              We work with the latest technologies to deliver modern, scalable solutions
            </p>
          </div>

          <div className="bg-white border border-gray-200 rounded-2xl p-12 shadow-lg fade-in">
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-6">
              {[
                "React", "Node.js", "Python", "AWS", "Docker", "MongoDB",
                "Vue.js", "TypeScript", "Java", "Azure", "Kubernetes", "PostgreSQL",
                "Angular", "Next.js", "C#", "GCP", "Jenkins", "Redis"
              ].map((tech, index) => (
                <div
                  key={index}
                  className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-center text-dark-red font-medium hover:bg-navy-blue hover:text-white transition-colors duration-300 fade-in"
                  style={{ animationDelay: `${index * 0.05}s` }}
                >
                  {tech}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
